import type { CSSProperties, ReactNode } from 'react';
import { SuiteSidebar } from './SuiteSidebar';
import type { SuiteAppConfig, SuiteNavItem } from './tokens';

export interface SuiteShellProps {
  /** Apps for the switcher (Suite Home + real apps). */
  apps: SuiteAppConfig[];
  /** Active app id — also drives the `--accent` color. */
  currentAppId: string;
  /** Selection handler for the switcher. */
  onSelectApp: (appId: string) => void;
  /** Nav items for the active app. */
  navItems: SuiteNavItem[];
  /** Current pathname for active-state computation. */
  pathname: string;
  /** Brand/logo slot. */
  logo?: ReactNode;
  /** User menu / identity slot. */
  userMenu?: ReactNode;
  /**
   * Extra content rendered as a sibling of the layout (e.g. CT's notification
   * dropdown, which must escape the sidebar's overflow to position itself).
   */
  overlay?: ReactNode;
  /**
   * Ambient background rendered behind the whole shell (e.g. CT's animated
   * particles). Sits at z-0, beneath the sidebar (z-10) and scrollable main.
   */
  background?: ReactNode;
  /** When true, the sidebar is collapsed (hidden). */
  sidebarCollapsed?: boolean;
  /** Page content. */
  children: ReactNode;
}

/**
 * Top-level suite layout: fixed sidebar + scrollable main. Sets the `--accent`
 * CSS custom property from the active app so descendant shell components can
 * theme via `color-mix(in srgb, var(--accent) …)`.
 *
 * The shell imports nothing app-specific — identity, notifications and links
 * arrive as config/slots.
 */
export function SuiteShell({
  apps,
  currentAppId,
  onSelectApp,
  navItems,
  pathname,
  logo,
  userMenu,
  overlay,
  background,
  sidebarCollapsed,
  children,
}: SuiteShellProps) {
  const current = apps.find((a) => a.id === currentAppId) ?? apps[0];
  const accent = current?.accent ?? '#0014cc';

  return (
    <div
      className="relative flex h-screen w-full overflow-hidden bg-slate-100 text-slate-800"
      style={{ ['--accent' as keyof CSSProperties]: accent } as CSSProperties}
    >
      {background && <div className="absolute inset-0 z-0">{background}</div>}
      <SuiteSidebar
        apps={apps}
        currentAppId={currentAppId}
        onSelectApp={onSelectApp}
        navItems={navItems}
        pathname={pathname}
        logo={logo}
        userMenu={userMenu}
        collapsed={sidebarCollapsed}
      />
      <main className="relative z-[1] flex-1 overflow-y-auto">{children}</main>
      {overlay}
    </div>
  );
}
